// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { ExpandableText } from "~/components/expandable-text";
import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { ShadowOperationIdBadge } from "~/modules/agave-shadow-renders/components/shadow-operation-id-badge";
import { fetchAgentProjectRunsInfiniteQuery } from "~/modules/agent-project-runs/queries";
import type { AgentProjectRun, AgentProjectRunStatus } from "~/modules/agent-project-runs/schemas";
import { agentProjectRunStatusSchema } from "~/modules/agent-project-runs/schemas";
import { formatRelativeTime } from "~/modules/videos/utils";

const statusFilterSchema = agentProjectRunStatusSchema.or(z.literal("all"));
const searchSchema = z.object({
  page: z.coerce.number().int().positive().max(100).optional().default(1).catch(1),
  status: statusFilterSchema.optional().default("all").catch("all"),
});

type StatusFilter = z.infer<typeof statusFilterSchema>;

const statusFilters: StatusFilter[] = ["all", "queued", "processing", "complete", "failed"];

export const Route = createFileRoute("/_dash/ai-edits/prompt-to-video")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { page, status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const targetPageIndex = Math.max(0, page - 1);
  const [currentPageIndex, setCurrentPageIndex] = useState(targetPageIndex);
  const params = useMemo(
    () => ({
      status: status === "all" ? undefined : status,
    }),
    [status]
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchAgentProjectRunsInfiniteQuery.useInfiniteQuery(
      {
        params,
      },
      {
        refetchOnWindowFocus: false,
        retry: false,
      }
    );

  const totalPages = data?.pages.length ?? 0;

  useEffect(() => {
    if (targetPageIndex >= totalPages && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [targetPageIndex, totalPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (targetPageIndex < totalPages) {
      setCurrentPageIndex(targetPageIndex);
      return;
    }
    if (totalPages > 0 && !hasNextPage && !isFetchingNextPage) {
      const lastPageIndex = totalPages - 1;
      setCurrentPageIndex(lastPageIndex);
      navigate({ search: { status, page: lastPageIndex + 1 }, replace: true });
    }
  }, [targetPageIndex, totalPages, hasNextPage, isFetchingNextPage, navigate, status]);

  const currentPageData = data?.pages[currentPageIndex];
  const isPagePending = !error && (isLoading || currentPageData == null);

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({ search: { status, page: previousPageIndex + 1 } });
  };

  const onNextPage = async () => {
    if (!hasNextPage && currentPageIndex >= totalPages - 1) {
      return;
    }

    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({ search: { status, page: nextPageIndex + 1 } });
    if (nextPageIndex >= totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigate({ search: { status, page: pageIndex + 1 } });
  };

  const onStatusChange = (value: string) => {
    navigate({ search: { status: value as StatusFilter, page: 1 } });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold">prompt to video</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[160px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusFilters.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load prompt-to-video runs</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isPagePending && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[9/14] w-full animate-none rounded-md" />
          ))}
        </div>
      )}

      {!isPagePending && currentPageData && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {currentPageData.results.page.map((run) => (
            <AgentProjectRunCard key={run.optimisticProjectId} run={run} />
          ))}
          {currentPageData.results.page.length === 0 && (
            <div className="rounded-md border p-8 text-center text-sm text-muted-foreground sm:col-span-2 lg:col-span-3 xl:col-span-4">
              No prompt-to-video runs found
            </div>
          )}
        </div>
      )}

      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}

function AgentProjectRunCard({ run }: { run: AgentProjectRun }) {
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-md border bg-card">
      <div className="relative bg-black">
        <RunVideoPlayer videoUrl={run.videoUrl} status={run.status} />
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 text-xs font-medium text-white">
          {formatRelativeTime(new Date(run.createdAt).getTime())}
        </div>
      </div>

      <div className="min-w-0 flex-1 space-y-2 p-3">
        {run.prompt && <ExpandableText value={run.prompt} className="text-foreground" />}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <RunStatusBadge status={run.status} />
          {run.mode && <Badge variant="outline">{run.mode}</Badge>}
          <RunDurationBadge run={run} />
          {run.seedAssetIds.length > 0 && (
            <Badge variant="outline">
              {run.seedAssetIds.length} seed {run.seedAssetIds.length === 1 ? "asset" : "assets"}
            </Badge>
          )}
          <ShadowOperationIdBadge value={run.operationId} className="flex-1 basis-32" />
        </div>
        {run.error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
            {run.errorCode && <p className="font-medium">{run.errorCode}</p>}
            {run.error}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          {run.userId ? (
            <button
              type="button"
              className="truncate font-mono text-[11px] text-muted-foreground hover:text-foreground"
              title={`Copy user id: ${run.userId}`}
              onClick={() => {
                void navigator.clipboard.writeText(run.userId ?? "");
              }}
            >
              user: {run.userId}
            </button>
          ) : (
            <span className="truncate font-mono text-[11px] text-muted-foreground">
              unknown user
            </span>
          )}
          <div className="flex shrink-0 items-center gap-3">
            <Link
              to="/ai-edits/agave-operation-graph"
              search={{ q: run.operationId }}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Graph
              <Icon name="link-out" className="size-3.5" />
            </Link>
            <Link
              to="/ai-edits/agave-operations"
              search={{ q: run.operationId }}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Operation details
              <Icon name="link-out" className="size-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function RunDurationBadge({ run }: { run: AgentProjectRun }) {
  const createdAtMs = new Date(run.createdAt).getTime();
  const isTerminal = run.status === "complete" || run.status === "failed";
  const endMs = isTerminal ? new Date(run.updatedAt).getTime() : Date.now();
  const durationMs = endMs - createdAtMs;
  if (!Number.isFinite(durationMs) || durationMs < 0) {
    return null;
  }
  return (
    <Badge
      variant="outline"
      title={isTerminal ? "Time from creation to completion" : "Elapsed since creation"}
    >
      {isTerminal ? formatDuration(durationMs) : `running ${formatDuration(durationMs)}`}
    </Badge>
  );
}

function formatDuration(durationMs: number) {
  const totalSeconds = Math.round(durationMs / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) {
    return seconds > 0 ? `${totalMinutes}m ${seconds}s` : `${totalMinutes}m`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function RunStatusBadge({ status }: { status: AgentProjectRunStatus }) {
  if (status === "failed") {
    return <Badge variant="destructive">failed</Badge>;
  }
  if (status === "complete") {
    return <Badge>complete</Badge>;
  }
  return <Badge variant="secondary">{status}</Badge>;
}

function RunVideoPlayer({
  videoUrl,
  status,
}: {
  videoUrl: string | null | undefined;
  status: AgentProjectRunStatus;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || shouldLoad) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px" }
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [shouldLoad]);

  return (
    <div ref={containerRef} className="flex aspect-[9/16] w-full items-center justify-center">
      {videoUrl ? (
        <video
          src={shouldLoad ? videoUrl : undefined}
          controls
          playsInline
          preload={shouldLoad ? "metadata" : "none"}
          className="h-full w-full object-contain"
        />
      ) : (
        <span className="px-4 text-center text-xs text-white/70">
          {status === "failed" ? "Failed" : "No video yet"}
        </span>
      )}
    </div>
  );
}
